import { type RefCallback, useContext, useMemo } from 'react';

import { useResizeObserver } from '../../../core/hooks/useResizeObserver.js';
import { SankeyColumnLabelsContext } from '../context/SankeyColumnLabels.context.js';
import { SankeyHighlightContext } from '../context/SankeyHighlight.context.js';
import { useSankeyLayout } from '../hooks/use-sankey-layout.js';
import {
  canvasAreaCell,
  canvasZone,
  graphAreaCell,
  graphAreaContainer,
} from '../SankeyChart.sty.js';
import {
  useSankeyHoveredShape,
  useSankeySelectedShape,
} from '../store/selectors.js';
import { getActiveConnectedSet } from '../utils/get-active-connected-set.js';
import { SankeyCanvas } from './SankeyCanvas.js';
import { SankeyColumnHeaders } from './SankeyColumnHeaders.js';
import { SankeyLabelOverlay } from './SankeyLabelOverlay.js';
import { SankeyOverlay } from './SankeyOverlay.js';

interface SankeyGraphAreaContentProps {
  outerRef: RefCallback<HTMLDivElement | null>;
  canvasRef: RefCallback<HTMLDivElement | null>;
  canvasWidth: number;
  outerWidth: number;
  height: number;
  maxSideLabelWidth: number | undefined;
  nodeColors: Map<string, string>;
}

export function SankeyGraphAreaContent({
  outerRef,
  canvasRef,
  canvasWidth,
  outerWidth,
  height,
  maxSideLabelWidth,
  nodeColors,
}: Readonly<SankeyGraphAreaContentProps>) {
  const layout = useSankeyLayout();
  const selected = useSankeySelectedShape();
  const hovered = useSankeyHoveredShape();
  const columnLabels = useContext(SankeyColumnLabelsContext);
  const { ref: leftCellRef, width: leftCellWidth } =
    useResizeObserver<HTMLDivElement>();

  const highlight = useMemo(
    () => getActiveConnectedSet(layout, selected, hovered),
    [layout, selected, hovered],
  );

  return (
    <>
      {columnLabels && (
        <SankeyColumnHeaders
          leftOffset={leftCellWidth ?? 0}
          outerWidth={outerWidth}
        />
      )}
      <SankeyHighlightContext.Provider value={highlight}>
        <div ref={outerRef} className={graphAreaContainer}>
          <div ref={leftCellRef} className={graphAreaCell}>
            <SankeyLabelOverlay
              zone="left"
              layout={layout}
              maxWidth={maxSideLabelWidth}
            />
          </div>
          <div ref={canvasRef} className={canvasAreaCell}>
            <div className={canvasZone}>
              <SankeyCanvas
                width={canvasWidth}
                height={height}
                layout={layout}
                nodeColors={nodeColors}
              />
              <SankeyLabelOverlay zone="inside" layout={layout} />
              <SankeyOverlay />
            </div>
          </div>
          <div className={graphAreaCell}>
            <SankeyLabelOverlay
              zone="right"
              layout={layout}
              maxWidth={maxSideLabelWidth}
            />
          </div>
        </div>
      </SankeyHighlightContext.Provider>
    </>
  );
}

SankeyGraphAreaContent['displayName'] = 'SankeyGraphAreaContent';
